import { useContext, useEffect, useState } from "react";
import {
    addMilliseconds,
    addMinutes,
    differenceInSeconds,
    intervalToDuration,
    isFuture,
    isWithinInterval,
} from "date-fns";
import { AdminAuctionContext } from "../context/AdminAuctionContext";
import useGetServerTimeOffset from "../../services/ServerTimeService";

const AuctionTimer = () => {
    const {
        currentAuctionEndTime: currentAuctionBidClose,
        currentAuctionStartTime: currentAuctionBidOpen,
        setAuctionState,
        onlineAuctionClosed,
        auctionPendingClose,
    } = useContext(AdminAuctionContext);

    const [timeRemaining, setTimeRemaining] = useState("");
    const [closeToClose, setCloseToClose] = useState(false);
    const {
        data: serverTimeOffset,
        isLoading: serverTimeLoading,
        isError: serverTimeError,
    } = useGetServerTimeOffset();

    useEffect(() => {
        if (!currentAuctionBidClose || !currentAuctionBidOpen) {
            return setTimeRemaining("Auction Not Started");
        }

        if (serverTimeError) {
            return setTimeRemaining("Error Loading Time");
        }

        if (serverTimeLoading) {
            return setTimeRemaining("Calculating...");
        }

        const interval = setInterval(() => {
            if (
                !isWithinInterval(
                    addMilliseconds(new Date(), serverTimeOffset),
                    {
                        start: new Date(currentAuctionBidOpen),
                        end: addMinutes(new Date(currentAuctionBidClose), 1),
                    }
                )
            ) {
                const afterNow = isFuture(new Date(currentAuctionBidOpen));
                clearInterval(interval);
                return setTimeRemaining(
                    afterNow ? "Auction Not Started" : "Auction Ended"
                );
            }

            const diff = intervalToDuration({
                start: addMilliseconds(new Date(), serverTimeOffset),
                end: new Date(currentAuctionBidClose),
            });

            const timeRemaining = differenceInSeconds(
                new Date(currentAuctionBidClose),
                addMilliseconds(new Date(), serverTimeOffset)
            );

            if (timeRemaining <= 0) {
                if (!auctionPendingClose && !onlineAuctionClosed) {
                    setAuctionState((p) => ({
                        ...p,
                        auctionPendingClose: true,
                    }));
                    return setTimeRemaining("Pending...");
                }
                if (onlineAuctionClosed) {
                    clearInterval(interval);
                    return setTimeRemaining("Auction Ended");
                }
                return;
            } else if (auctionPendingClose) {
                setAuctionState((p) => ({
                    ...p,
                    auctionPendingClose: false,
                }));
            }
            if (timeRemaining < 60) {
                setCloseToClose(true);
            } else {
                setCloseToClose(false);
            }

            let formatted = "";
            let highestUnit = -1;

            ["years", "months", "days", "hours", "minutes", "seconds"].forEach(
                (unit, i) => {
                    let amount = diff[unit];
                    if (amount === undefined) {
                        return;
                    }
                    if (amount <= 0 && highestUnit === -1) {
                        return;
                    }
                    highestUnit = i;
                    formatted += (amount + "").padStart(2, "0");
                    if (unit !== "seconds") {
                        formatted += ":";
                    }
                }
            );

            setTimeRemaining(formatted);
        }, 1000);

        return () => clearInterval(interval);
    }, [
        currentAuctionBidClose,
        currentAuctionBidOpen,
        serverTimeOffset,
        serverTimeError,
        serverTimeLoading,
        onlineAuctionClosed,
        setAuctionState,
        auctionPendingClose,
    ]);

    return (
        <div className="flex items-center text-gray-600 ml-2">
            <p className="text-sm md:text-base">
                Time Remaining:{" "}
                <span
                    className={`font-bold text-gray-900 ml-1 ${
                        timeRemaining === "Auction Ended" || closeToClose
                            ? "text-cta"
                            : "text-gray-900 "
                    }`}
                >
                    {timeRemaining}
                </span>
            </p>
        </div>
    );
};

export default AuctionTimer;
